import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Helmet } from 'react-helmet-async';
import { Layout } from '@/components/layout/Layout';
import { SEO } from '@/components/seo/SEO';
import { FAQSection } from '@/components/seo/FAQSection';
import { HOMEPAGE_FAQS } from '@/lib/homepageFaqs';
import { HomeHero } from '@/components/home/v2/HomeHero';
import { CompactNewsSection } from '@/components/home/CompactNewsSection';
import { UpcomingEventsWidget } from '@/components/home/UpcomingEventsWidget';
import { useHomepageWidgets } from '@/hooks/useSiteSettings';
import { GoalDiscovery } from '@/components/home/v2/GoalDiscovery';
import { ToolsWorthExploring } from '@/components/home/v2/ToolsWorthExploring';
import { RecentlyAdded, ExploreCategories } from '@/components/home/v2/DiscoveryFeed';
import { CompareSection, AccountSection, CreatorStrip } from '@/components/home/v2/CompareAndAccount';
import { RecentlyViewed } from '@/components/tools/RecentlyViewed';
import { NewsletterSubscribe } from '@/components/newsletter/NewsletterSubscribe';
import { FeaturedToolsBanner } from '@/components/ads/FeaturedToolsBanner';
import { RotatingPromoBanner } from '@/components/promo/RotatingPromoBanner';
import { useTools } from '@/hooks/useTools';
import { PlaybookFeature } from '@/components/home/v2/PlaybookFeature';

export default function Index() {
  const [searchParams] = useSearchParams();
  const legacyCategory = searchParams.get('category');
  const { data: featuredTools = [], isLoading: featuredLoading } = useTools({ featured: true });
  const { showEventsWidget } = useHomepageWidgets();

  // Old "/?category=x" links now go to the dedicated category page (the full directory left the homepage).
  if (legacyCategory && /^[a-z0-9-]{1,60}$/.test(legacyCategory)) {
    return <Navigate to={`/categories/${legacyCategory}`} replace />;
  }

  const featured = featuredTools.slice(0, 10);

  return (
    <Layout>
      <SEO />
      {featured.length > 0 && (
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'ItemList',
              name: 'Featured AI Tools',
              numberOfItems: featured.length,
              itemListElement: featured.map((t, i) => ({
                '@type': 'ListItem',
                position: i + 1,
                item: {
                  '@type': 'SoftwareApplication',
                  name: t.name,
                  url: `https://aitoolhub.ai/tool/${t.slug}`,
                  image: t.logo_url || undefined,
                  applicationCategory: 'AI',
                },
              })),
            })}
          </script>
        </Helmet>
      )}

      <HomeHero />
      <GoalDiscovery />
      <ToolsWorthExploring tools={featured} isLoading={featuredLoading} />
      <PlaybookFeature />
      <RecentlyAdded excludeIds={featured.map((t) => t.id)} />

      <div className="container mx-auto px-4 pt-10 text-center">
        <Button size="lg" variant="outline" asChild>
          <Link to="/tools">Explore All AI Tools <ArrowRight className="h-4 w-4 ml-1" /></Link>
        </Button>
      </div>

      <div className="pt-10">
        <RotatingPromoBanner />
      </div>


      <FeaturedToolsBanner placementSlug="homepage-hero" title="Sponsored" className="container mx-auto px-4 pt-4" />

      <ExploreCategories />
      <CompareSection />
      <AccountSection />
      <CreatorStrip />

      <RecentlyViewed />

      {/* Compact FAQ (first 5 keep their structured data); full list on /faq */}
      <section className="container mx-auto px-4 py-12">
        <div className="max-w-3xl mx-auto">
          <FAQSection title="Frequently Asked Questions" faqs={HOMEPAGE_FAQS.slice(0, 5)} />
          <Link to="/faq" className="inline-flex items-center mt-4 text-sm font-medium text-primary hover:underline">
            More questions <ArrowRight className="h-4 w-4 ml-1" />
          </Link>
        </div>
      </section>

      {/* Upcoming AI conferences, shown above the news row */}
      {showEventsWidget && (
        <section className="container mx-auto px-4 py-6">
          <div className="max-w-2xl mx-auto">
            <UpcomingEventsWidget />
          </div>
        </section>
      )}

      {/* Compact "Latest in AI" headlines linking to /news */}
      <CompactNewsSection />

      {/* Newsletter stays a separate compact row so neither card stretches */}
      <section className="container mx-auto px-4 pb-20">
        <div className="max-w-6xl mx-auto">
          <div className="rounded-2xl border border-border/60 p-6 max-w-2xl mx-auto">
            <p className="font-medium">AI Tool Digest</p>
            <p className="text-sm text-muted-foreground mt-1 mb-4">Weekly launches and picks worth trying. No spam.</p>
            <NewsletterSubscribe />
          </div>
        </div>
      </section>
    </Layout>
  );
}
